import { createFileRoute, useParams, useSearch } from "@tanstack/react-router";
import { searchAsStrings } from "@/components/portal/apps/reporting/custom/columnFilterParams";

import { RequireAuth } from "@/components/RequireAuth";
import { PortalDataShell } from "@/components/portal/PortalShell";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { useAuth } from "@/contexts/AuthContext";
import { Spinner } from "@/components/ui/spinner";
import { ViewReportContent } from "@/components/portal/apps/reporting/custom/ViewReportContent";

/**
 * Standalone full-screen view for a reporting custom report (including
 * pivot — pivot is a render mode inside this same page, not a separate
 * route) — no sidebar/header portal chrome, opened via the "Open in new
 * window" button next to the existing same-tab CSS Fullscreen toggle
 * (`CustomReportView.tsx`). Escapes the normal `/portal` layout route (the
 * `portal_` file-name prefix strips layout nesting, not the URL path — see
 * `portal_.sign-in.tsx`), same pattern as the Spaces widget window route.
 *
 * No route-level `RequirePermission` here, matching the EXISTING
 * `/portal/apps/reporting/custom/$slug` route: reporting access is enforced
 * by `ViewReportContent` itself via the backend's object-permission check
 * (it renders its own `PermissionDenied` card on a 403), not a blanket
 * RBAC gate — adding one here would be MORE restrictive than today's
 * in-portal page. See the
 * spaces-viewer-mobile-responsive-and-fullscreen-window plan (D6/D9).
 *
 * `validateSearch` mirrors `custom/$slug.tsx`'s open filter-key → value map
 * so a link built from the current filters seeds the same values here.
 * Numbers and booleans come back as text (`searchAsStrings`): the router
 * parses every value as JSON, so `cf.Year.eq=2026` would otherwise arrive as
 * a number and be dropped (#1411).
 */
type ReportWindowSearch = Record<string, string>;

export const Route = createFileRoute("/portal_/apps/reporting/custom/$slug/window")({
  component: ReportWindowPage,
  validateSearch: (search: Record<string, unknown>): ReportWindowSearch => searchAsStrings(search),
});

function ReportWindowPage() {
  return (
    <RequireAuth>
      <AuthedReportWindowPage />
    </RequireAuth>
  );
}

function AuthedReportWindowPage() {
  const { session } = useAuth();
  if (!session) return null; // RequireAuth redirects before this can render
  return (
    <PortalDataShell session={session}>
      <ReportWindowContent />
    </PortalDataShell>
  );
}

function ReportWindowContent() {
  const { slug } = useParams({ from: "/portal_/apps/reporting/custom/$slug/window" });
  const search = useSearch({ from: "/portal_/apps/reporting/custom/$slug/window" });
  const { selectedOrganisation } = usePortalData();

  // ViewReportContent itself needs orgId (via usePortalData) before its own
  // fetch can fire — same transient-spinner tick the Spaces widget window
  // route has while PortalDataProvider resolves the org list.
  if (!selectedOrganisation?.organisationId) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <Spinner />
      </div>
    );
  }

  // No sizing wrapper needed: CustomReportView's `startFullscreen`-seeded
  // overlay is `fixed inset-0 z-50` (CustomReportView.tsx) — position:fixed
  // escapes normal layout flow, so it doesn't care about a parent's
  // height/padding. An `h-dvh` wrapper here would be dead styling.
  return <ViewReportContent slug={slug} initialFilterValues={search} startFullscreen />;
}
